import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { DateRangeType } from "react-tailwindcss-datepicker";

import CustomDatePicker from "../../components/awaitingInventory/CustomDatePicker";
import { LinkButton } from "../../components/shared/Inputs/LinkButton";
import PillTab from "../../components/shared/PillTab/PillTab";
import PillTabMenu from "../../components/shared/PillTab/PillTabMenu";
import WasteTrackingTable from "../../components/wasteTracking/WasteTrackingTable";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { WasteReceiptView } from "../../models/wasteTracking/IWasteReceiptRow";

const views: { key: WasteReceiptView; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "submitted", label: "Submitted" },
  { key: "excluded", label: "Excluded" },
];

// DEFRA Receipt of Waste for every collected vehicle. Pending = collected, no receipt yet;
// fill the columns (or bulk update), then submit. Feature flag "DEFRA" gates the route and
// the sidebar item; the API refuses everything without it too.
export default function WasteTracking() {
  const { setInfo } = usePageNameContext();
  const [view, setView] = useState<WasteReceiptView>("pending");
  const [readyOnly, setReadyOnly] = useState(false);
  const [dates, setDates] = useState<DateRangeType>({
    startDate: DateTime.now().minus({ days: 30 }).startOf("day").toJSDate(),
    endDate: null,
  });

  useEffect(() => {
    setInfo({
      name: "Waste Tracking",
      desc: "Submit DEFRA receipt of waste movements for collected vehicles",
    });
  }, []);

  const additionalBody = useMemo(() => {
    const toIso = (value: Date | string | null | undefined, endOfDay: boolean) => {
      if (!value) return null;
      const dt = DateTime.fromJSDate(new Date(value), { zone: "Europe/London" });
      return (endOfDay ? dt.endOf("day") : dt.startOf("day")).toUTC().toISO();
    };
    return {
      startDate: toIso(dates.startDate, false),
      endDate: toIso(dates.endDate, true),
      readyOnly: view === "pending" && readyOnly,
    };
  }, [dates, view, readyOnly]);

  return (
    <>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
        <PillTabMenu className="!mt-0">
          {views.map((v) => (
            <PillTab key={v.key} label={v.label} active={view === v.key} onClick={() => setView(v.key)} />
          ))}
        </PillTabMenu>
        <div className="flex flex-wrap items-end gap-4">
          {view === "pending" && (
            <div className="mb-1 flex items-center">
              <input
                id="wasteReadyOnly"
                type="checkbox"
                className="mr-2 h-5 w-5"
                checked={readyOnly}
                onChange={(e) => setReadyOnly(e.target.checked)}
              />
              <label htmlFor="wasteReadyOnly">Ready only</label>
            </div>
          )}
          <LinkButton label="Hauliers" to="hauliers" classes="mb-0.5" />
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">{view === "submitted" ? "Submitted between" : view === "excluded" ? "Excluded between" : "Collected between"}</span>
            <CustomDatePicker dates={dates} handleDatesChange={setDates} />
          </div>
        </div>
      </div>

      <WasteTrackingTable view={view} additionalBody={additionalBody} />
    </>
  );
}
